import React from 'react';
import { FlexColumn } from 'design-system';
import { choiceValueFinishesSurvey } from '../../../helpers/surveyFinishSurvey';
import DirectorMessage from './options/DirectorMessage';
import ChoiceOptionExtras from './options/ChoiceOptionExtras';
import QuestionNotesAndImage from './options/QuestionNotesAndImage';
import DropdownOptions from './options/DropdownOptions';
import EmojiOptions from './options/EmojiOptions';
import EmployeeOptions from './options/EmployeeOptions';
import GridTypeAnswer from './options/GridTypeAnswer';
import GridSpotAnswer from './options/GridSpotAnswer';
import InventoryTypeAnswer from './options/InventoryTypeAnswer';
import MultiSelectOption from './options/MultiSelectOption';
import NumberTypeAnswer from './options/NumberTypeAnswer';
import RadioOptions from './options/RadioOptions';
import RatingOptions from './options/RatingOptions';
import SignatureTypeAnswer from './options/SignatureTypeAnswer';
import TextTypeAnswer from './options/TextTypeAnswer';
import UploadFile from './options/UploadFile';
import { useSurveyPreviewQuestionAnswer } from './SurveyPreviewContext';

function supportsChoiceExtras(questionType, questionTypes) {
  return [
    questionTypes.RADIO,
    questionTypes.MULTI,
    questionTypes.SKILLS,
    questionTypes.SEQUENCE,
    questionTypes.DROPDOWN,
  ].includes(questionType);
}

export default function SurveyQuestionInput({
  question,
  questionTypes,
  survey,
  recipient,
  preview = false,
  hasError = false,
  employeesLoading = false,
  onMinError,
  onMaxError,
  onClearErrors,
  onFinishSurvey,
}) {
  const {
    value: answer,
    writeIn,
    note,
    images,
    setValue: setAnswer,
    setWriteIn,
    setNote,
    setImages,
    interactive,
  } = useSurveyPreviewQuestionAnswer(question?._id);
  const isInteractive = preview && interactive;
  const showChoiceExtras = supportsChoiceExtras(question?.questionType, questionTypes);
  const showNotesAndImage =
    question?.questionType !== 'director_message' &&
    (question?.allow_comment || question?.allow_file_upload);

  const handleFinishableChange = (nextValue) => {
    setAnswer(nextValue);
    if (onFinishSurvey && choiceValueFinishesSurvey(question, nextValue)) {
      onFinishSurvey(question, nextValue);
    }
  };

  const choiceExtras =
    showChoiceExtras && isInteractive ? (
      <ChoiceOptionExtras
        question={question}
        value={answer}
        writeIn={writeIn}
        images={images}
        onWriteInChange={setWriteIn}
        onImagesChange={setImages}
        interactive={isInteractive}
      />
    ) : null;

  const notesAndImage =
    showNotesAndImage && isInteractive ? (
      <QuestionNotesAndImage
        key={question?._id}
        question={question}
        survey={survey}
        note={note}
        images={images}
        onNoteChange={setNote}
        onImagesChange={setImages}
        interactive={isInteractive}
      />
    ) : null;

  return (
    <>
      {question.questionType === questionTypes.TEXT ? (
        <TextTypeAnswer
          question={question}
          preview={preview}
          value={answer}
          onChange={setAnswer}
          interactive={isInteractive}
          hasError={hasError}
        />
      ) : null}

      {question.questionType === questionTypes.NUMBER ? (
        <NumberTypeAnswer
          preview={preview}
          value={answer}
          onChange={setAnswer}
          interactive={isInteractive}
          hasError={hasError}
        />
      ) : null}

      {question.questionType === questionTypes.DROPDOWN ? (
        <FlexColumn align="stretch" fullWidth>
          <DropdownOptions
            options={question.options}
            preview={preview}
            value={answer}
            onChange={handleFinishableChange}
            interactive={isInteractive}
            hasError={hasError}
          />
          {choiceExtras}
        </FlexColumn>
      ) : null}

      {question.questionType === questionTypes.SIGNATURE ? (
        <SignatureTypeAnswer
          preview={preview}
          value={answer}
          onChange={setAnswer}
          interactive={isInteractive}
        />
      ) : null}

      {question.questionType === questionTypes.GRID ? (
        <GridTypeAnswer
          question={question}
          preview={preview}
          value={answer}
          onChange={setAnswer}
          interactive={isInteractive}
        />
      ) : null}

      {question.questionType === questionTypes.GRIDSPOT ? (
        <GridSpotAnswer
          question={question}
          preview={preview}
          value={answer}
          onChange={setAnswer}
          interactive={isInteractive}
        />
      ) : null}

      {question.questionType === questionTypes.INVENTORY ? (
        <InventoryTypeAnswer
          question={question}
          survey={survey}
          preview={preview}
          value={answer}
          onChange={setAnswer}
          interactive={isInteractive}
          hasError={hasError}
        />
      ) : null}

      {question.questionType === questionTypes.EMPLOYEES ? (
        <EmployeeOptions
          preview={preview}
          value={answer}
          onChange={setAnswer}
          interactive={isInteractive}
          loading={employeesLoading}
        />
      ) : null}

      {question.questionType === questionTypes.FILE ? (
        <FlexColumn align="stretch" className="mt-3">
          <UploadFile
            preview={preview}
            value={answer}
            onChange={setAnswer}
            interactive={isInteractive}
          />
        </FlexColumn>
      ) : null}

      {[questionTypes.RATING, questionTypes.STAR_RATING].includes(question.questionType) ? (
        <RatingOptions
          options={question.options}
          question={question}
          isStarRating={question.questionType === questionTypes.STAR_RATING}
          preview={preview}
          value={answer}
          onChange={
            question.questionType === questionTypes.STAR_RATING
              ? setAnswer
              : handleFinishableChange
          }
          interactive={isInteractive}
        />
      ) : null}

      {question.questionType === questionTypes.RADIO ? (
        <FlexColumn align="stretch" fullWidth>
          <RadioOptions
            options={question.options}
            preview={preview}
            value={answer}
            onChange={handleFinishableChange}
            interactive={isInteractive}
          />
          {choiceExtras}
        </FlexColumn>
      ) : null}

      {[questionTypes.MULTI, questionTypes.SKILLS, questionTypes.SEQUENCE].includes(question.questionType) ? (
        <FlexColumn align="stretch" fullWidth>
          <MultiSelectOption
            options={question.options}
            question={question}
            preview={preview}
            value={answer}
            onChange={setAnswer}
            interactive={isInteractive}
            showSequence={question.questionType === questionTypes.SEQUENCE}
            onMinError={onMinError}
            onMaxError={onMaxError}
            onClearErrors={onClearErrors}
          />
          {choiceExtras}
        </FlexColumn>
      ) : null}

      {question.questionType === questionTypes.EMOJI ? (
        <EmojiOptions
          small
          options={question.options}
          preview={preview}
          value={answer}
          onChange={handleFinishableChange}
          interactive={isInteractive}
        />
      ) : null}

      {question.questionType === 'director_message' ? (
        <DirectorMessage recipient={recipient} leadershipUser={survey?.leadershipUser} />
      ) : null}

      {notesAndImage}
    </>
  );
}
